<template>
  <div class="file-upload-container">
    <div class="mt-3">
      <!-- FILE DROP AREA -->
      <FileDrop
        id="bulk_creation_spreadsheet_file_input"
        :message="i18n.t('messages.dropYourExcelOrCsvFileHereOrBrowseYourComputer')"
        @file-drop="processFiles" />
    </div>

    <!-- TEMPLATE FILES -->
    <template v-if="worksheet == null && allowToDownloadTemplate">
      <form-separator opacity="100%">{{ $t('messages.startingFromScratchDownloadATemplate') }}</form-separator>
      <div
        class="actions flex-h-centered"
        style="align-items: flex-start">
        <a :href="modelParser?.templatePath">
          <button class="btn btn-sm btn-default-hollow">
            <icon-file-x-l-s />
            <span>{{ capitalize($t('actions.downloadXLSXTemplate')) }}</span>
          </button>
        </a>
      </div>
    </template>

    <PanelTemplate
      :icon="markRaw(IconInfo)"
      color="info">
      {{ i18n.t('messages.missingFieldsInCreateWithSpreadsheet') }}
    </PanelTemplate>

    <div
      v-if="showWorksheetSelector"
      :style="{ 'min-height': `${20 + 4 * workbook?.worksheets.length}rem` }">
      <form-separator opacity="100%">{{ $t('messages.selectWorksheet') }}</form-separator>

      <WorksheetSelector
        :workbook="workbook"
        :model-value="worksheet"
        @update:model-value="onWorksheetChanged" />
    </div>

    <!-- INVALID FILE ALERT-->
    <PanelTemplate
      v-if="error"
      :color="'danger'"
      :icon="markRaw(IconError)">
      {{ error }}
    </PanelTemplate>
    <PanelTemplate
      v-else-if="isCompleted"
      :color="'success'"
      :icon="markRaw(IconSuccess)">
      {{ i18n.t('messages.fileMatchesTemplate') }}
    </PanelTemplate>
  </div>
</template>

<script setup lang="ts" generic="T">
  import Excel from 'exceljs';
  import { capitalize, computed, markRaw, onMounted, Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import { SpreadSheetModelParser } from '@/common/components/controls/table/bulk-create/model-parser/ModelParser';
  import WorksheetSelector from '@/common/components/controls/table/Spreadsheet/WorksheetSelector.vue';
  import FileDrop from '@/common/components/templates/FileDrop.vue';
  import PanelTemplate from '@/common/components/templates/PanelTemplate.vue';
  import { IconError, IconFileXLS, IconInfo, IconSuccess } from '@/common/icons/monochrome';

  const i18n = useI18n();

  interface IProps {
    modelParser: SpreadSheetModelParser<T>;
    allowToDownloadTemplate?: boolean;
  }

  const { modelParser, allowToDownloadTemplate = true } = defineProps<IProps>();

  interface IEmits {
    (event: 'worksheetChanged', worksheet: Excel.Worksheet): void;
  }

  const emits = defineEmits<IEmits>();

  const isProcessing = ref(false);
  const error: Ref<string> = ref(null);
  const showWorksheetSelector = ref(false);
  const workbook = ref<Excel.Workbook>(null);
  const worksheet = ref<Excel.Worksheet>(null);

  const isCompleted = computed(() => {
    return isProcessing.value === false && error.value == null && worksheet.value != null;
  });

  function processFiles(fileList: File[]) {
    isProcessing.value = true;
    error.value = null;
    const [fileToUpload] = Object.keys(fileList).map((x) => fileList[x]);

    const excelMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const validFileTypes = [excelMimeType];

    if (!validFileTypes.includes(fileToUpload.type)) {
      error.value = i18n.t('messages.invalidFileType');
      return;
    }

    const fileReader = new window.FileReader();
    fileReader.onload = (e) => {
      isProcessing.value = false;
      if (fileToUpload.type === excelMimeType) {
        validateExcelFile(e.target.result as Excel.Buffer);
      }
    };
    fileReader.onerror = () => {
      isProcessing.value = false;
      error.value = i18n.t('messages.errorReadingFile');
    };
    fileReader.readAsArrayBuffer(fileToUpload);
  }

  async function validateExcelFile(buffer: Excel.Buffer) {
    workbook.value = new Excel.Workbook();
    showWorksheetSelector.value = false;
    await workbook.value.xlsx.load(buffer);
    if (workbook.value.worksheets.length === 0) {
      error.value = i18n.t('messages.fileIsEmpty');
      return;
    }

    if (workbook.value.worksheets.length > 1) {
      // Check if the workbook has the correct worksheet
      const worksheet = workbook.value.getWorksheet(modelParser.worksheetName);
      if (worksheet != null) {
        onWorksheetChanged(worksheet);
        return;
      }
      showWorksheetSelector.value = true;
      return;
    }
    const worksheet = workbook.value.getWorksheet(1);
    onWorksheetChanged(worksheet);
  }

  async function onWorksheetChanged(selectedWorksheet: Excel.Worksheet) {
    isProcessing.value = true;
    try {
      if (selectedWorksheet == null) {
        error.value = i18n.t('messages.noWorksheetSelected');
        return;
      }
      error.value = await modelParser.validateWorksheet(selectedWorksheet);
      if (error.value != null) {
        return;
      }
      worksheet.value = selectedWorksheet;
      emits('worksheetChanged', worksheet.value);
    } finally {
      isProcessing.value = false;
    }
  }

  watch(worksheet, () => {
    emits('worksheetChanged', worksheet.value);
  });

  onMounted(() => {
    workbook.value = null;
    worksheet.value = null;
    error.value = null;
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  .file-upload-container {
    margin: 0 25%;
  }

  a {
    text-decoration: none;
  }
</style>
